import React, { useEffect, useState } from "react";
import { MultiSelect, Loader } from "@mantine/core";
import type {
  FieldComponentProps,
  ReviewItemData,
} from "@/pages/ClientProfile/types";
import { OPTIONS_MAP } from "@smartwiz/shared-types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import { showFieldError } from "@utils/reviewFieldUtils.ts";

interface MultiSelectFieldProps extends FieldComponentProps {
  options?: string[];
  item: { value: string[] | null } & Pick<
    ReviewItemData,
    "type" | "key" | "label" | "indentLevel"
  >;
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  commonProps,
  field,
  options,
  item,
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );

  const optionKey = options?.[0];
  const selectOptions =
    optionKey && typeof optionKey === "string" && OPTIONS_MAP[optionKey]
      ? OPTIONS_MAP[optionKey]
      : (options ?? []);

  const initialValue = Array.isArray(item.value)
    ? item.value
    : Array.isArray(field.value)
      ? field.value
      : [];

  const [loadingState, setLoadingState] = useState(false);
  const [optimisticValue, setOptimisticValue] =
    useState<string[]>(initialValue);

  useEffect(() => {
    const updated =
      Array.isArray(item.value) || Array.isArray(field.value)
        ? (item.value ?? field.value ?? [])
        : [];
    setOptimisticValue(updated);
  }, [item.value, field.value]);

  const handleChange = (values: string[]): void => {
    setOptimisticValue(values);
    field.onChange(values);
  };

  const handleBlur = async (): Promise<void> => {
    field.onBlur();
    if (!Array.isArray(optimisticValue)) return;

    setLoadingState(true);

    try {
      await updateProfileField(userId ?? "", field.name, optimisticValue);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      setOptimisticValue(
        Array.isArray(field.value) ? field.value : initialValue
      );
      setLoadingState(false);
    }
  };

  return (
    <MultiSelect
      {...commonProps}
      {...field}
      value={optimisticValue}
      onChange={handleChange}
      onBlur={handleBlur}
      data={selectOptions.map((option) => ({
        value: option,
        label: option,
      }))}
      variant="filled"
      searchable
      clearable
      nothingFoundMessage="Nothing found"
      hidePickedOptions
      maxDropdownHeight={300}
      disabled={loadingState}
      rightSection={
        loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
      }
    />
  );
};

export default MultiSelectField;
